// The numbers of the Orders page, from order-overview/order-count over all
// dates, like the list (the figures the old metrics cards showed):
// - orderHeaderStats: under the headings (and as chips on phones); a stat
//   with a filter applies the same value as the heading pop-up when clicked.
// - withCounts: the per-value counts next to the heading pop-up options
//   (order types, fulfilment statuses, billing statuses).

const lower = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const count = (value) => Number(value) || 0;

const titleCase = (value) =>
  value.replace(/\b\w/g, (char) => char.toUpperCase());

const numberFormat = new Intl.NumberFormat("en-US");

export const formatCount = (value) => numberFormat.format(count(value));

export const orderHeaderStats = (counts) => {
  if (!counts) return {};

  const unpaid = count(
    counts.by_fulfillment_billing_status?.find(
      (item) => lower(item?.fulfillment_billing_status) === "unpaid",
    )?.total_orders,
  );
  const statusCount = (start) =>
    (counts.by_status ?? [])
      .filter((item) => lower(item?.status).startsWith(start))
      .reduce((sum, item) => sum + count(item?.total_orders), 0);

  return {
    order: [
      {
        key: "orders",
        label: `${formatCount(counts.total_order_count)} orders`,
        title: "All orders",
      },
    ],
    shipping: [
      {
        key: "shipped",
        label: `${formatCount(counts.orders_related_to_shipments)} shipped`,
        title: "Orders with a Bolt shipment",
      },
      {
        key: "delivered",
        label: `${formatCount(counts.delivered_shipments_order_count)} delivered`,
        tone: "success",
        title: "Delivered shipments: click to show them",
        filter: { field_name: "shipment_status", value: "delivered" },
      },
    ],
    fulfilment: [
      {
        key: "new",
        label: `${formatCount(statusCount("pending"))} new`,
        title: "Fulfilment Pending / New: click to show them",
        filter: { field_name: "fulfillment_status", value: "pending" },
      },
      {
        key: "unpaid",
        label: `${formatCount(unpaid)} unpaid`,
        tone: "danger",
        title: "Unpaid fulfilment fees: click to show them",
        filter: { field_name: "fulfillment_billing_status", value: "unpaid" },
      },
      {
        key: "attention",
        label: `${formatCount(statusCount("attention"))} attention`,
        tone: "warning",
        title: "Fulfilment needs attention: click to show them",
        filter: { field_name: "fulfillment_status", value: "attention" },
      },
    ],
  };
};

/**
 * Heading pop-up options with their count (all dates), matched by
 * value: order types exactly ("self pickup"), fulfilment statuses by start
 * ("pending" counts "pending / new"), billing statuses exactly; a value
 * with no orders shows 0. Values found in the counts but not in the list are
 * appended, so nothing the old metrics cards showed is left out.
 */
export const withCounts = (options, counts, kind) => {
  if (!counts) return options;
  const rows = {
    type: (counts.by_type ?? []).map((item) => [
      item?.type,
      item?.total_orders,
    ]),
    fulfilment: (counts.by_status ?? []).map((item) => [
      item?.status,
      item?.total_orders,
    ]),
    billing: (counts.by_fulfillment_billing_status ?? []).map((item) => [
      item?.fulfillment_billing_status,
      item?.total_orders,
    ]),
  }[kind];
  if (!rows) return options;

  const matchesOption = (name, option) =>
    kind === "fulfilment"
      ? lower(name).startsWith(lower(option.value))
      : lower(name) === lower(option.value);

  const listed = options.map((option) => ({
    ...option,
    count: formatCount(
      rows
        .filter(([name]) => matchesOption(name, option))
        .reduce((sum, [, total]) => sum + count(total), 0),
    ),
  }));

  // Values in the data that the fixed list doesn't have (e.g. "Import"
  // orders from the Import button, "amazon"), as the old metrics cards
  // listed every value: added after the list, with their count.
  const extras = new Map();
  rows.forEach(([name, total]) => {
    const value = lower(name);
    if (!value || options.some((option) => matchesOption(name, option))) {
      return;
    }
    const extra = extras.get(value) ?? {
      label: titleCase(value),
      value,
      total: 0,
    };
    extra.total += count(total);
    extras.set(value, extra);
  });

  return [
    ...listed,
    ...[...extras.values()].map(({ label, value, total }) => ({
      label,
      value,
      count: formatCount(total),
    })),
  ];
};
